<template>
  <div
    v-if="selectedProduct"
    translate="no"
    id="product-modal-container"
    class="product-modal-container-top-screen"
  >
    <div id="product-modal" class="modal-bigger" ref="productModal">
      <div id="header-and-content-product-container" ref="productModalContent">
        <div id="edit-product-modal-header">
          <div id="product-name-and-close-btn">
            <h3 id="selected-product-name">{{ selectedProduct.names.translation[language].name}}</h3>
            <div @click="onCloseClick" id="close-product-modal">
              <font-awesome-icon id="product-times" icon="times" />
            </div>
          </div>
        </div>

        <div id="edit-product-content">
          <div id="photo-section" class="form-section">
            <div v-if="photoDisplaySrc" class="photo-preview-container">
              <clip-loader
                v-if="isPhotoLoading"
                :loading="isPhotoLoading"
                size="30px"
                color="#003340"
              />
              <img
                v-show="!isPhotoLoading"
                :src="photoDisplaySrc"
                alt="Φωτογραφία προϊόντος"
                id="product-photo-preview"
                @load="isPhotoLoading = false"
                @error="isPhotoLoading = false"
              />
            </div>
            <div v-else class="photo-placeholder">Δεν υπάρχει φωτογραφία</div>

            <input
              type="file"
              accept="image/*"
              ref="photoFileInput"
              style="display:none"
              @change="onPhotoFileSelected"
            />

            <div class="photo-buttons-row">
              <button type="button" class="photo-action-button" @click="triggerPhotoFileInput">
                <font-awesome-icon icon="image" style="margin-right:6px" />
                {{ photoDisplaySrc ? 'Αλλαγή φωτογραφίας' : 'Ανέβασμα φωτογραφίας' }}
              </button>
              <button
                v-if="photoDisplaySrc"
                type="button"
                class="photo-action-button remove-photo-button"
                @click="onRemovePhoto"
              >
                <font-awesome-icon icon="trash" style="margin-right:6px" />Αφαίρεση φωτογραφίας
              </button>
            </div>
          </div>

          <div id="category-section" class="form-section">
            <h4 class="section-title">Κατηγορία</h4>
            <div class="field-row">
              <select class="text-input" v-model.number="form.categoryId">
                <option
                  v-for="category in categories"
                  :key="category.categoryId"
                  :value="category.categoryId"
                >{{category.names.translation.GRE.name}}</option>
              </select>
            </div>
          </div>

          <div id="names-and-descriptions-section" class="form-section">
            <h4 class="section-title">Ονόματα &amp; περιγραφές ανά γλώσσα</h4>
            <div v-for="lang in languages" :key="lang.code" class="language-row">
              <span class="language-badge">{{lang.label}}</span>
              <div class="language-inputs">
                <input
                  type="text"
                  class="text-input"
                  :placeholder="`Όνομα (${lang.label})`"
                  v-model="form.names[lang.code].name"
                />
                <textarea
                  class="text-input textarea-input"
                  :placeholder="`Περιγραφή (${lang.label})`"
                  v-model="form.names[lang.code].details"
                ></textarea>
              </div>
            </div>
          </div>

          <div id="availability-section" class="form-section">
            <h4 class="section-title">Διαθεσιμότητα</h4>
            <div class="toggle-row">
              <span class="toggle-label">Διαθέσιμο</span>
              <toggle-button v-model="form.available" :sync="true" color="#198b4a" />
            </div>
            <div v-if="!form.available" class="toggle-row">
              <span class="toggle-label">Εμφάνιση σαν μη διαθέσιμο</span>
              <toggle-button v-model="form.shownWhenUnavailable" :sync="true" color="#198b4a" />
            </div>
            <div v-if="!form.available" class="toggle-row">
              <span class="toggle-label">Αυτόματη επανενεργοποίηση το πρωί</span>
              <toggle-button
                v-model="form.automaticReactivatingAvailability"
                :sync="true"
                color="#198b4a"
              />
            </div>
          </div>

          <div id="pricing-section" class="form-section">
            <h4 class="section-title">Τιμολόγηση</h4>
            <div class="field-row">
              <label class="field-label">Τιμή</label>
              <input
                type="number"
                min="0"
                step="0.01"
                :disabled="!form.priceable"
                class="text-input number-input"
                v-model.number="form.price"
              />
            </div>
            <div class="field-row">
              <label class="field-label">Τιμή take away</label>
              <input
                type="number"
                min="0"
                step="0.01"
                :disabled="!form.priceable"
                class="text-input number-input"
                v-model.number="form.takeAwayPrice"
              />
            </div>
            <div class="field-row">
              <label class="field-label">Τιμή delivery</label>
              <input
                type="number"
                min="0"
                step="0.01"
                :disabled="!form.priceable"
                class="text-input number-input"
                v-model.number="form.deliveryPrice"
              />
            </div>
            <div v-if="form.memberPriceEnabled" class="field-row">
              <label class="field-label">Τιμή μέλους</label>
              <input
                type="number"
                min="0"
                step="0.01"
                :disabled="!form.priceable"
                class="text-input number-input"
                v-model.number="form.membershipPrice"
              />
            </div>
            <div v-if="restaurant.membershipEnabled" class="toggle-row">
              <span class="toggle-label">Ενεργή τιμή μέλους</span>
              <toggle-button v-model="form.memberPriceEnabled" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Ανοιχτή τιμή</span>
              <toggle-button v-model="form.openPrice" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Πώληση ανά κιλό</span>
              <toggle-button v-model="form.perKg" :sync="true" color="#198b4a" />
            </div>
            <div v-if="restaurant.hasTimePriceableAssets" class="toggle-row">
              <span class="toggle-label">Τιμή υπολογιζόμενη ανά ώρα</span>
              <toggle-button v-model="form.priceCalculatedPerHour" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Έχει τιμή</span>
              <toggle-button v-model="form.priceable" :sync="true" color="#198b4a" />
            </div>
          </div>

          <div id="vat-section" class="form-section">
            <h4 class="section-title">ΦΠΑ &amp; MyData</h4>
            <div class="field-row">
              <label class="field-label">ΦΠΑ (%)</label>
              <select class="text-input number-input" v-model="form.vatPercentage">
                <option :value="null">Δεν έχει οριστεί</option>
                <option :value="0">Άνευ ΦΠΑ 0%</option>
                <option :value="13">13%</option>
                <option :value="24">24%</option>
                <option :value="4">4%</option>
                <option :value="6">6%</option>
                <option :value="9">9%</option>
                <option :value="17">17%</option>
              </select>
            </div>
            <div class="field-row">
              <label class="field-label">ΦΠΑ take away/delivery (%)</label>
              <select class="text-input number-input" v-model="form.takeAwayDeliveryVatPercentage">
                <option :value="null">Δεν έχει οριστεί</option>
                <option :value="0">Άνευ ΦΠΑ 0%</option>
                <option :value="13">13%</option>
                <option :value="24">24%</option>
                <option :value="4">4%</option>
                <option :value="6">6%</option>
                <option :value="9">9%</option>
                <option :value="17">17%</option>
              </select>
            </div>
            <div class="field-row">
              <label class="field-label">Πρόσθετος φόρος</label>
              <select class="text-input" v-model="form.extraTax">
                <option :value="null">Χωρίς επιπλέον φόρους</option>
                <option
                  value="SAVE_ENVIROMENT_FROM_PLASTIC"
                >Προστασία από πλαστικά προϊόντα (5 cents)</option>
                <option
                  value="DOUBLE_SAVE_ENVIROMENT_FROM_PLASTIC"
                >Διπλό τέλος προστασίας από πλαστικά προϊόντα (10 cents)</option>
              </select>
            </div>
            <div class="field-row">
              <label class="field-label">Πρόσθετος φόρος take away/delivery</label>
              <select class="text-input" v-model="form.extraTaxDeliveryTakeAway">
                <option :value="null">Χωρίς επιπλέον φόρους</option>
                <option
                  value="SAVE_ENVIROMENT_FROM_PLASTIC"
                >Προστασία από πλαστικά προϊόντα (5 cents)</option>
                <option
                  value="DOUBLE_SAVE_ENVIROMENT_FROM_PLASTIC"
                >Διπλό τέλος προστασίας από πλαστικά προϊόντα (10 cents)</option>
              </select>
            </div>
            <div class="field-row">
              <label class="field-label">Χαρακτηρισμός εσόδου</label>
              <select class="text-input" v-model="form.myDataClassificationCategory">
                <option value="category1_1">Έσοδα από Πώληση Εμπορευμάτων</option>
                <option value="category1_2">Έσοδα από Πώληση Προϊόντων</option>
                <option value="category1_3">Έσοδα από Παροχή Υπηρεσιών</option>
              </select>
            </div>
          </div>

          <div id="dietary-section" class="form-section">
            <h4 class="section-title">Διαιτητικά χαρακτηριστικά</h4>
            <div class="toggle-row">
              <span class="toggle-label">Χορτοφαγικό</span>
              <toggle-button v-model="form.vegetarian" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Vegan</span>
              <toggle-button v-model="form.vegan" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Χωρίς γλουτένη</span>
              <toggle-button v-model="form.glutenFree" :sync="true" color="#198b4a" />
            </div>
          </div>

          <div id="stock-section" class="form-section">
            <h4 class="section-title">Απόθεμα</h4>
            <div class="toggle-row">
              <span class="toggle-label">Ενεργό απόθεμα</span>
              <toggle-button v-model="form.stockEnabled" :sync="true" color="#198b4a" />
            </div>
            <div v-if="form.stockEnabled" class="schedule-details">
              <div class="field-row">
                <label class="field-label">Ποσότητα αποθέματος</label>
                <input
                  type="number"
                  min="0"
                  class="text-input number-input"
                  v-model.number="form.stockAmount"
                />
              </div>
              <div class="field-row">
                <label class="field-label">Προειδοποίηση όταν απομένουν</label>
                <input
                  type="number"
                  min="0"
                  class="text-input number-input"
                  v-model.number="form.stockAmountToSendWarning"
                />
              </div>
              <div class="toggle-row">
                <span class="toggle-label">Μη διαθέσιμο όταν εξαντληθεί το απόθεμα</span>
                <toggle-button
                  v-model="form.unavailableIfStockFinished"
                  :sync="true"
                  color="#198b4a"
                />
              </div>
              <div class="toggle-row">
                <span class="toggle-label">Απόθεμα συγχρονισμένο με προσθήκη στο καλάθι</span>
                <toggle-button
                  v-model="form.stockAlignedToCartAddition"
                  :sync="true"
                  color="#198b4a"
                />
              </div>
            </div>
          </div>

          <div id="options-section" class="form-section">
            <h4 class="section-title">Επιλογές</h4>
            <div class="toggle-row">
              <span class="toggle-label">Μετρήσιμο προϊόν</span>
              <toggle-button v-model="form.isMeasurable" :sync="true" color="#198b4a" />
            </div>
            <!-- <div class="toggle-row">
              <span class="toggle-label">Αλλαγή ονόματος στο συγχρονισμό με Wiorder</span>
              <toggle-button v-model="form.nameChangeOnWiorderSync" :sync="true" color="#198b4a" />
            </div>-->
            <div class="toggle-row">
              <span class="toggle-label">Έχει σχόλιο</span>
              <toggle-button v-model="form.isCommentAvailable" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Μόνο για PDA</span>
              <toggle-button v-model="form.pdaProductOnly" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Είναι αίτημα</span>
              <toggle-button v-model="form.request" :sync="true" color="#198b4a" />
            </div>
          </div>

          <!-- <div id="integrations-section" class="form-section">
            <h4 class="section-title">Ενσωματώσεις</h4>
            <div class="field-row">
              <label class="field-label">Wiorder Product ID</label>
              <input
                type="number"
                class="text-input number-input"
                v-model.number="form.wiProductId"
                placeholder="κανένα"
              />
            </div>
            <div class="field-row">
              <label class="field-label">Glimpse Product ID</label>
              <input
                type="number"
                class="text-input number-input"
                v-model.number="form.glimpseProductId"
                placeholder="κανένα"
              />
            </div>
            <div class="field-row">
              <label class="field-label">Glimpse Size ID</label>
              <input
                type="number"
                class="text-input number-input"
                v-model.number="form.glimpseSizeId"
                placeholder="κανένα"
              />
            </div>
            <div class="field-row">
              <label class="field-label">Glimpse Product Size ID</label>
              <input
                type="number"
                class="text-input number-input"
                v-model.number="form.glimpseProductSizeId"
                placeholder="κανένα"
              />
            </div>
          </div>-->

          <div id="appearance-section" class="form-section">
            <h4 class="section-title">Εμφάνιση</h4>
            <div class="field-row">
              <label class="field-label">Χρώμα φόντου</label>
              <div class="color-field">
                <input
                  type="color"
                  class="color-picker"
                  :value="bgColorForPicker"
                  @input="form.bgColor = $event.target.value"
                />
                <input
                  type="text"
                  disabled
                  class="text-input color-text"
                  v-model="form.bgColor"
                  placeholder="προεπιλογή"
                />
                <!-- <button
                  type="button"
                  class="clear-color-button"
                  @click="form.bgColor = ''"
                >Καθαρισμός</button>-->
              </div>
            </div>
            <div class="field-row">
              <label class="field-label">Χρώμα γραμμάτων</label>
              <select class="text-input color-text" v-model="form.textColor">
                <option value>Προεπιλογή</option>
                <option value="white">Λευκό</option>
                <option :value="null">Μαύρο</option>
              </select>
            </div>
            <!-- <div class="field-row">
              <label class="field-label">Σύνδεσμος φωτογραφίας</label>
              <input
                type="text"
                class="text-input"
                v-model="form.photoLink"
                placeholder="https://..."
              />
            </div>-->
            <div class="field-row">
              <label class="field-label">Σειρά εμφάνισης</label>
              <select
                class="text-input number-input"
                :disabled="isCreatingProduct"
                v-model.number="form.viewPriority"
              >
                <option
                  v-for="option in viewPriorityOptions"
                  :key="option"
                  :value="option"
                >{{option}}</option>
              </select>
            </div>
          </div>

          <div id="subselections-section" class="form-section">
            <h4 class="section-title">Υποεπιλογές προϊόντος</h4>
            <div class="subselections-columns">
              <div class="subselections-column" ref="availableSubCategoriesColumn">
                <h5 class="subselections-column-title">Διαθέσιμες κατηγορίες</h5>
                <div
                  v-if="availableSubCategories.length === 0"
                  class="subselections-empty"
                >Δεν υπάρχουν άλλες διαθέσιμες κατηγορίες</div>
                <div
                  v-for="subCategory in availableSubCategories"
                  :key="subCategory.id"
                  class="subselection-row"
                >
                  <div class="subselection-info">
                    <span class="subselection-name">{{subCategoryName(subCategory)}}</span>
                    <span v-if="subCategory.alias" class="subselection-alias">
                      <font-awesome-icon
                        icon="clipboard-check"
                        style="margin-right:2px; margin-top:2px"
                      />
                      {{subCategory.alias}}
                    </span>
                    <span
                      class="subselection-mandatory"
                    >{{subCategory.mandatory ? 'Υποχρεωτική' : 'Προαιρετική'}}</span>
                  </div>
                  <button
                    type="button"
                    class="subselection-button add-subselection-button"
                    @click="addSubCategory(subCategory.id)"
                  >
                    <font-awesome-icon icon="plus" />
                  </button>
                </div>
              </div>

              <div class="subselections-column" ref="selectedSubCategoriesColumn">
                <h5 class="subselections-column-title">Υποεπιλογές προϊόντος</h5>
                <div
                  v-if="selectedSubCategories.length === 0"
                  class="subselections-empty"
                >Δεν έχουν προστεθεί υποεπιλογές</div>
                <transition-group name="subselection-move" tag="div">
                  <div
                    v-for="(subCategory, index) in selectedSubCategories"
                    :key="subCategory.id"
                    class="subselection-row"
                    :class="{ 'subselection-row-highlight': subCategory.id === highlightedSubCategoryId }"
                  >
                    <div class="subselection-info">
                      <span class="subselection-name">{{subCategoryName(subCategory)}}</span>
                      <span v-if="subCategory.alias" class="subselection-alias">
                        <font-awesome-icon
                          icon="clipboard-check"
                          style="margin-right:2px; margin-top:2px"
                        />
                        {{subCategory.alias}}
                      </span>
                      <span
                        class="subselection-mandatory"
                      >{{subCategory.mandatory ? 'Υποχρεωτική' : 'Προαιρετική'}}</span>
                      <div
                        v-if="selectedSubCategories.length > 1"
                        class="subselection-reorder-buttons"
                      >
                        <button
                          v-if="index > 0"
                          type="button"
                          class="subselection-reorder-button"
                          @click="moveSubCategoryUp(subCategory.id)"
                        >
                          <font-awesome-icon icon="chevron-up" />
                        </button>
                        <button
                          v-if="index < selectedSubCategories.length - 1"
                          type="button"
                          class="subselection-reorder-button"
                          @click="moveSubCategoryDown(subCategory.id)"
                        >
                          <font-awesome-icon icon="chevron-down" />
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="subselection-button remove-subselection-button"
                      @click="removeSubCategory(subCategory.id)"
                    >
                      <font-awesome-icon icon="minus" />
                    </button>
                  </div>
                </transition-group>
              </div>
            </div>
          </div>
        </div>

        <div id="edit-product-modal-footer">
          <p v-if="error" id="error-message">{{errorMessage}}</p>
          <div id="bottom-buttons">
            <button
              id="cancel-product-button"
              class="footer-button"
              :disabled="isSaving"
              @click="onCloseClick"
            >Ακύρωση</button>
            <button
              id="save-product-button"
              class="footer-button"
              :disabled="isSaving"
              @click="onSaveClick"
            >
              <clip-loader v-if="isSaving" :loading="isSaving" size="16px" color="#ffffff" />
              <font-awesome-icon v-else icon="check" />
              {{isSaving ? 'Αποθήκευση...' : 'Αποθήκευση'}}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MenuApi from "../api/menu";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";

const LANGUAGES = [
  { code: "GRE", label: "Ελληνικά" },
  { code: "ENG", label: "Αγγλικά" },
];

export default {
  name: "EditProductModal",
  components: {
    ClipLoader,
  },
  props: [
    "selectedProduct",
    "productCategoryId",
    "productsCount",
    "isCreatingProduct",
    "subCategoriesOfMenu",
    "categories",
    "restaurantId",
    "storeId",
    "restaurant",
    "authToken",
    "jwt",
  ],
  data() {
    return {
      languages: LANGUAGES,
      form: this.buildEmptyForm(),
      isSaving: false,
      isPhotoLoading: true,
      selectedPhotoFile: null,
      photoPreviewUrl: null,
      highlightedSubCategoryId: null,
      error: false,
      errorMessage: "",
    };
  },
  watch: {
    "form.photoLink"() {
      this.isPhotoLoading = true;
    },

    selectedProduct: {
      immediate: true,
      handler(product) {
        this.form = this.isCreatingProduct ? this.buildNewProductForm() : this.buildFormFromProduct(product);
        this.resetPhotoSelection();
        this.error = false;
        this.errorMessage = "";
        this.scrollModalToTop();
        this.scrollSubCategoryColumnsToTop();
      },
    },

    isModalOpen(newVal) {
      if (newVal) {
        this.form = this.isCreatingProduct ? this.buildNewProductForm() : this.buildFormFromProduct(this.selectedProduct);
        this.resetPhotoSelection();
        this.error = false;
        this.errorMessage = "";
        this.scrollModalToTop();
        this.scrollSubCategoryColumnsToTop();
      }
    },

    "form.priceable"(newVal) {
      if (!newVal) {
        this.form.price = 0;
        this.form.takeAwayPrice = 0;
        this.form.deliveryPrice = 0;
        this.form.membershipPrice = 0;
      }
    },
  },
  computed: {
    language() {
      return this.$store.getters.getLanguage;
    },

    isModalOpen() {
      return this.$store.state.editProductModalOpen;
    },

    bgColorForPicker() {
      return /^#[0-9a-fA-F]{6}$/.test(this.form.bgColor) ? this.form.bgColor : "#ffffff";
    },

    photoDisplaySrc() {
      return this.photoPreviewUrl || this.form.photoLink;
    },

    viewPriorityOptions() {
      let productsCount = this.productsCount || 0;
      let options = [];
      for (let i = 1; i <= productsCount; i++) {
        options.push(i);
      }
      if (this.form.viewPriority != null && !options.includes(this.form.viewPriority)) {
        options.push(this.form.viewPriority);
        options.sort((a, b) => a - b);
      }
      return options;
    },

    availableSubCategories() {
      let subCategoriesOfMenu = this.subCategoriesOfMenu || [];
      return this.form.availableOrder
        .map((id) => subCategoriesOfMenu.find((subCategory) => subCategory.id === id))
        .filter((subCategory) => subCategory != null);
    },

    selectedSubCategories() {
      let subCategoriesOfMenu = this.subCategoriesOfMenu || [];
      return this.form.subselectionCategoryIds
        .map((id) => subCategoriesOfMenu.find((subCategory) => subCategory.id === id))
        .filter((subCategory) => subCategory != null);
    },
  },
  methods: {
    buildEmptyForm() {
      return {
        categoryId: null,
        names: {
          GRE: { name: "", details: "" },
          ENG: { name: "", details: "" },
        },
        price: null,
        takeAwayPrice: null,
        deliveryPrice: null,
        vatPercentage: null,
        extraTax: null,
        extraTaxDeliveryTakeAway: null,
        takeAwayDeliveryVatPercentage: null,
        myDataClassificationCategory: "",
        memberPriceEnabled: false,
        isMeasurable: false,
        isCommentAvailable: false,
        vegetarian: false,
        vegan: false,
        glutenFree: false,
        priceCalculatedPerHour: false,
        nameChangeOnWiorderSync: false,
        pdaProductOnly: false,
        priceable: true,
        perKg: false,
        openPrice: false,
        stockEnabled: false,
        unavailableIfStockFinished: false,
        stockAlignedToCartAddition: false,
        request: false,
        available: true,
        shownWhenUnavailable: true,
        automaticReactivatingAvailability: true,
        wiProductId: null,
        stockAmount: null,
        stockAmountToSendWarning: null,
        glimpseProductId: null,
        glimpseSizeId: null,
        glimpseProductSizeId: null,
        membershipPrice: 0,
        bgColor: "",
        textColor: null,
        photoLink: "",
        viewPriority: 0,
        subselectionCategoryIds: [],
        availableOrder: this.buildAvailableOrder([]),
      };
    },

    buildAvailableOrder(selectedIds) {
      let subCategoriesOfMenu = this.subCategoriesOfMenu || [];
      return subCategoriesOfMenu.filter((subCategory) => !selectedIds.includes(subCategory.id)).map((subCategory) => subCategory.id);
    },

    buildNewProductForm() {
      let form = this.buildEmptyForm();
      form.categoryId = this.productCategoryId;
      form.priceable = true;
      form.isMeasurable = true;
      form.isCommentAvailable = true;
      form.available = true;
      form.shownWhenUnavailable = true;
      form.automaticReactivatingAvailability = true;
      form.viewPriority = (this.productsCount || 0) + 1;
      return form;
    },

    buildFormFromProduct(product) {
      let emptyForm = this.buildEmptyForm();
      if (!product) {
        return emptyForm;
      }

      let names = {};
      this.languages.forEach((lang) => {
        let translation = product.names && product.names.translation ? product.names.translation[lang.code] : null;
        names[lang.code] = {
          name: translation && translation.name ? translation.name : "",
          details: translation && translation.details ? translation.details : "",
        };
      });

      let subselectionCategoryIds = (product.subselections || []).map((subselection) => subselection.id);

      return {
        categoryId: this.productCategoryId,
        names,
        price: product.price != null ? product.price : 0,
        takeAwayPrice: product.takeAwayPrice != null ? product.takeAwayPrice : 0,
        deliveryPrice: product.deliveryPrice != null ? product.deliveryPrice : 0,
        vatPercentage: product.vatPercentage != null ? product.vatPercentage : null,
        extraTax: product.extraTax != null ? product.extraTax : null,
        extraTaxDeliveryTakeAway: product.extraTaxDeliveryTakeAway != null ? product.extraTaxDeliveryTakeAway : null,
        takeAwayDeliveryVatPercentage:
          product.takeAwayDeliveryVatPercentage != null ? product.takeAwayDeliveryVatPercentage : null,
        myDataClassificationCategory: product.myDataClassificationCategory || "",
        memberPriceEnabled: !!product.memberPriceEnabled,
        isMeasurable: product.measurable != null ? !!product.measurable : !!product.isMeasurable,
        isCommentAvailable: product.commentAvailable != null ? !!product.commentAvailable : !!product.isCommentAvailable,
        vegetarian: !!product.vegetarian,
        vegan: !!product.vegan,
        glutenFree: !!product.glutenFree,
        priceCalculatedPerHour: !!product.priceCalculatedPerHour,
        nameChangeOnWiorderSync: !!product.nameChangeOnWiorderSync,
        pdaProductOnly: !!product.pdaProductOnly,
        priceable: product.priceable != null ? !!product.priceable : true,
        perKg: !!product.perKg,
        openPrice: !!product.openPrice,
        stockEnabled: !!product.stockEnabled,
        unavailableIfStockFinished: !!product.unavailableIfStockFinished,
        stockAlignedToCartAddition: !!product.stockAlignedToCartAddition,
        request: !!product.request,
        available: product.available != null ? !!product.available : true,
        shownWhenUnavailable: product.shownWhenUnavailable != null ? !!product.shownWhenUnavailable : true,
        automaticReactivatingAvailability:
          product.automaticReactivatingAvailability != null ? !!product.automaticReactivatingAvailability : true,
        wiProductId: product.wiProductId != null ? product.wiProductId : null,
        stockAmount: product.stockAmount != null ? product.stockAmount : null,
        stockAmountToSendWarning: product.stockAmountToSendWarning != null ? product.stockAmountToSendWarning : null,
        glimpseProductId: product.glimpseProductId != null ? product.glimpseProductId : null,
        glimpseSizeId: product.glimpseSizeId != null ? product.glimpseSizeId : null,
        glimpseProductSizeId: product.glimpseProductSizeId != null ? product.glimpseProductSizeId : null,
        membershipPrice: product.membershipPrice != null ? product.membershipPrice : 0,
        bgColor: product.bgColor || "",
        textColor: product.textColor || null,
        photoLink: product.photoLink || "",
        viewPriority: product.viewPriority != null ? product.viewPriority : 0,
        subselectionCategoryIds: subselectionCategoryIds,
        availableOrder: this.buildAvailableOrder(subselectionCategoryIds),
      };
    },

    onCloseClick() {
      this.$store.dispatch("closeEditProductModal");
    },

    subCategoryName(subCategory) {
      return subCategory.names && subCategory.names.translation && subCategory.names.translation.GRE
        ? subCategory.names.translation.GRE.name
        : "";
    },

    addSubCategory(subCategoryId) {
      if (!this.form.subselectionCategoryIds.includes(subCategoryId)) {
        this.form.subselectionCategoryIds.push(subCategoryId);
      }
      this.form.availableOrder = this.form.availableOrder.filter((id) => id !== subCategoryId);
      this.scrollColumnToBottom("selectedSubCategoriesColumn");
    },

    removeSubCategory(subCategoryId) {
      this.form.subselectionCategoryIds = this.form.subselectionCategoryIds.filter((id) => id !== subCategoryId);
      if (!this.form.availableOrder.includes(subCategoryId)) {
        this.form.availableOrder.push(subCategoryId);
      }
      this.scrollColumnToBottom("availableSubCategoriesColumn");
    },

    moveSubCategoryUp(subCategoryId) {
      let ids = this.form.subselectionCategoryIds;
      let index = ids.indexOf(subCategoryId);
      if (index > 0) {
        let newIds = ids.slice();
        newIds[index] = newIds[index - 1];
        newIds[index - 1] = subCategoryId;
        this.form.subselectionCategoryIds = newIds;
        this.highlightSubCategory(subCategoryId);
      }
    },

    moveSubCategoryDown(subCategoryId) {
      let ids = this.form.subselectionCategoryIds;
      let index = ids.indexOf(subCategoryId);
      if (index >= 0 && index < ids.length - 1) {
        let newIds = ids.slice();
        newIds[index] = newIds[index + 1];
        newIds[index + 1] = subCategoryId;
        this.form.subselectionCategoryIds = newIds;
        this.highlightSubCategory(subCategoryId);
      }
    },

    highlightSubCategory(subCategoryId) {
      this.highlightedSubCategoryId = subCategoryId;
      setTimeout(() => {
        if (this.highlightedSubCategoryId === subCategoryId) {
          this.highlightedSubCategoryId = null;
        }
      }, 700);
    },

    scrollColumnToBottom(refName) {
      this.$nextTick(() => {
        let el = this.$refs[refName];
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    scrollSubCategoryColumnsToTop() {
      this.$nextTick(() => {
        if (this.$refs.availableSubCategoriesColumn) {
          this.$refs.availableSubCategoriesColumn.scrollTop = 0;
        }
        if (this.$refs.selectedSubCategoriesColumn) {
          this.$refs.selectedSubCategoriesColumn.scrollTop = 0;
        }
      });
    },

    scrollModalToTop() {
      try {
        this.$nextTick(() => {
          if (this.$refs.productModal) {
            this.$refs.productModal.scrollTop = 0;
          }
          if (this.$refs.productModalContent) {
            this.$refs.productModalContent.scrollTop = 0;
          }
        });
      } catch (error) {
        console.log("Could not scroll product modal to top");
      }

    },

    resetPhotoSelection() {
      if (this.photoPreviewUrl) {
        URL.revokeObjectURL(this.photoPreviewUrl);
      }
      this.selectedPhotoFile = null;
      this.photoPreviewUrl = null;
    },

    triggerPhotoFileInput() {
      this.$refs.photoFileInput.click();
    },

    resizeImageFile(file, maxDimension) {
      if (file.type === "image/gif") {
        return Promise.resolve(file);
      }

      return new Promise((resolve) => {
        let objectUrl = URL.createObjectURL(file);
        let image = new Image();

        image.onload = () => {
          let scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
          URL.revokeObjectURL(objectUrl);

          if (scale === 1) {
            resolve(file);
            return;
          }

          let canvas = document.createElement("canvas");
          canvas.width = Math.round(image.width * scale);
          canvas.height = Math.round(image.height * scale);
          canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);

          canvas.toBlob((blob) => {
            resolve(blob ? new File([blob], file.name, { type: file.type }) : file);
          }, file.type);
        };

        image.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
        };

        image.src = objectUrl;
      });
    },

    async onPhotoFileSelected(event) {
      let file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      event.target.value = "";
      if (!file) {
        return;
      }

      let resizedFile = await this.resizeImageFile(file, 750);

      if (this.photoPreviewUrl) {
        URL.revokeObjectURL(this.photoPreviewUrl);
      }
      this.selectedPhotoFile = resizedFile;
      this.photoPreviewUrl = URL.createObjectURL(resizedFile);
      this.isPhotoLoading = true;
    },

    onRemovePhoto() {
      this.resetPhotoSelection();
      this.form.photoLink = null;
    },

    async onSaveClick() {
      if (this.isSaving) {
        return;
      }

      if (!this.form.names.GRE.name || this.form.names.GRE.name.trim().length === 0) {
        this.error = true;
        this.errorMessage = "Το ελληνικό όνομα του προϊόντος είναι υποχρεωτικό";
        return;
      }

      if (this.form.price === "" || this.form.price === null || this.form.price === undefined) {
        this.error = true;
        this.errorMessage = "Η τιμή του προϊόντος είναι υποχρεωτική";
        return;
      }

      this.error = false;
      this.errorMessage = "";
      this.isSaving = true;

      let greName = this.form.names.GRE.name;
      let greDetails = this.form.names.GRE.details;
      let engName =
        this.form.names.ENG.name && this.form.names.ENG.name.trim().length > 0 ? this.form.names.ENG.name : greName;
      let engDetails =
        this.form.names.ENG.details && this.form.names.ENG.details.trim().length > 0
          ? this.form.names.ENG.details
          : greDetails;

      let takeAwayPrice =
        this.form.takeAwayPrice === "" || this.form.takeAwayPrice === null || this.form.takeAwayPrice === undefined
          ? this.form.price
          : this.form.takeAwayPrice;
      let deliveryPrice =
        this.form.deliveryPrice === "" || this.form.deliveryPrice === null || this.form.deliveryPrice === undefined
          ? this.form.price
          : this.form.deliveryPrice;
      let takeAwayDeliveryVatPercentage =
        this.form.takeAwayDeliveryVatPercentage === "" ||
          this.form.takeAwayDeliveryVatPercentage === null ||
          this.form.takeAwayDeliveryVatPercentage === undefined
          ? this.form.vatPercentage
          : this.form.takeAwayDeliveryVatPercentage;

      let payload = {
        names: {
          translation: {
            GRE: { name: greName, details: greDetails },
            ENG: { name: engName, details: engDetails },
          },
        },
        price: this.form.price,
        takeAwayPrice: takeAwayPrice,
        deliveryPrice: deliveryPrice,
        vatPercentage: this.form.vatPercentage,
        extraTax: this.form.extraTax,
        extraTaxDeliveryTakeAway: this.form.extraTaxDeliveryTakeAway,
        takeAwayDeliveryVatPercentage: takeAwayDeliveryVatPercentage,
        myDataClassificationCategory: this.form.myDataClassificationCategory || null,
        memberPriceEnabled: this.form.memberPriceEnabled,
        isMeasurable: this.form.isMeasurable,
        isCommentAvailable: this.form.isCommentAvailable,
        vegetarian: this.form.vegetarian,
        vegan: this.form.vegan,
        glutenFree: this.form.glutenFree,
        priceCalculatedPerHour: this.form.priceCalculatedPerHour,
        nameChangeOnWiorderSync: this.form.nameChangeOnWiorderSync,
        pdaProductOnly: this.form.pdaProductOnly,
        priceable: this.form.priceable,
        perKg: this.form.perKg,
        openPrice: this.form.openPrice,
        stockEnabled: this.form.stockEnabled,
        unavailableIfStockFinished: this.form.unavailableIfStockFinished,
        stockAlignedToCartAddition: this.form.stockAlignedToCartAddition,
        request: this.form.request,
        isAvailable: this.form.available,
        isShownWhenUnavailable: this.form.shownWhenUnavailable,
        isAutomaticReactivatingAvailability: this.form.automaticReactivatingAvailability,
        wiProductId: this.form.wiProductId === "" ? null : this.form.wiProductId,
        stockAmount: this.form.stockAmount === "" ? null : this.form.stockAmount,
        stockAmountToSendWarning: this.form.stockAmountToSendWarning === "" ? null : this.form.stockAmountToSendWarning,
        glimpseProductId: this.form.glimpseProductId === "" ? null : this.form.glimpseProductId,
        glimpseSizeId: this.form.glimpseSizeId === "" ? null : this.form.glimpseSizeId,
        glimpseProductSizeId: this.form.glimpseProductSizeId === "" ? null : this.form.glimpseProductSizeId,
        membershipPrice: this.form.membershipPrice,
        bgColor: this.form.bgColor === "" ? null : this.form.bgColor,
        textColor: this.form.textColor === "" ? null : this.form.textColor,
        photoLink: this.form.photoLink === "" ? null : this.form.photoLink,
        viewPriority: this.form.viewPriority,
      };

      try {
        let productId;
        if (this.isCreatingProduct) {
          let createdProduct = await MenuApi.createProduct(
            this.restaurantId,
            this.form.categoryId,
            payload,
            this.authToken,
            this.jwt
          );
          productId = createdProduct ? createdProduct.id || createdProduct.productId : null;
        } else {
          productId = this.selectedProduct.id;
          await MenuApi.editProduct(
            this.restaurantId,
            productId,
            this.form.categoryId,
            payload,
            this.authToken,
            this.jwt
          );
        }

        if (this.selectedPhotoFile && productId != null) {
          await MenuApi.uploadProductPhoto(
            this.restaurantId,
            this.storeId,
            productId,
            this.selectedPhotoFile,
            this.authToken,
            this.jwt
          );
        }

        if (productId != null) {
          await MenuApi.updateProductSubselections(
            this.restaurantId,
            productId,
            this.form.subselectionCategoryIds,
            this.authToken,
            this.jwt
          );
        }

        this.$emit("category-updated");
        this.$store.dispatch("closeEditProductModal");
      } catch (error) {
        this.error = true;
        this.errorMessage =
          error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Παρουσιάστηκε σφάλμα κατά την αποθήκευση του προϊόντος";
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
#product-modal-container {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  z-index: 205 !important;
}

.product-modal-container-top-screen {
  margin-top: 1vh;
  align-items: flex-start;
}

#product-modal {
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 98vw;
  height: 98vh;
  min-width: 200px;
  min-height: 97%;
  max-height: 95%;
  background: #f5f5f5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.16), 0 2px 4px rgba(0, 0, 0, 0.23);
  overflow-y: scroll;

  opacity: 1;
  border-radius: 12px;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  z-index: 202 !important;
}

#product-modal::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

#header-and-content-product-container {
  overflow-x: hidden;
}

#close-product-modal {
  position: sticky;
  top: 2%;
  left: 93%;
  background: white;
  border-radius: 100%;
  min-height: 4.5vh;
  height: 40% !important;
  width: 4.5vh;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 4px;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.24), 2px 2px 4px rgba(0, 0, 0, 0.36);
}

#edit-product-modal-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-evenly;
  flex-shrink: 0;
  width: 100%;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.24), 0 2px 4px rgba(0, 0, 0, 0.36);
  position: sticky;
  top: 0;
  z-index: 20;
  padding-bottom: 10px;
}

#product-name-and-close-btn {
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: flex-end;
  justify-content: space-between;
}

#edit-product-content {
  display: flex;
  flex-direction: column;
  padding: 12px 6px 90px 6px;
}

#error-message {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #d9534f;
}

.form-section {
  background: white;
  border-radius: 10px;
  padding: 12px 6px;
  margin-bottom: 14px;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1), 2px 2px 4px rgba(0, 0, 0, 0.14);
}

#photo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.photo-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60px;
  width: 100%;
}

#product-photo-preview {
  max-width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: 8px;
}

.photo-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 45px;
  color: #6c757d;
  font-size: 14px;
  font-style: italic;
}

.photo-buttons-row {
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.photo-action-button {
  display: flex;
  align-items: center;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #0275d8;
  color: white;
}

.remove-photo-button {
  background: #dc3545;
}

.section-title {
  margin: 0 0 10px 0;
  font-size: 15px;
  font-weight: 700;
  color: #003340;
}

.language-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.language-row:last-child {
  margin-bottom: 0;
}

.language-badge {
  flex-shrink: 0;
  width: 84px;
  padding: 4px 6px;
  border-radius: 6px;
  background: #003340;
  color: white;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.language-inputs {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-grow: 1;
}

.text-input {
  box-shadow: inset 0px 0px 2px rgba(116, 135, 150, 0.2),
    inset 1px 1px 2px rgba(116, 135, 150, 0.36);
  border-radius: 6px;
  border: none;
  padding: 6px 8px;
  font-size: 14px;
  background: #f7f7f7;
  width: 100%;
  box-sizing: border-box;
}

.text-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.textarea-input {
  resize: vertical;
  min-height: 44px;
  font-family: inherit;
}

.field-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.field-row:last-child {
  margin-bottom: 0;
}

.field-label {
  width: 260px;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  color: #343a40;
}

.color-field {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.color-picker {
  width: 40px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.clear-color-button {
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  background: #e2e2e2;
  color: #343a40;
  flex-shrink: 0;
}

.color-text,
.number-input {
  max-width: 140px;
}

select.text-input {
  -webkit-appearance: auto;
  cursor: pointer;
}

.toggle-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #ececec;
}

.toggle-row:last-child {
  border-bottom: none;
}

.toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #343a40;
}

.schedule-details {
  padding-left: 12px;
  margin-top: 6px;
  border-left: 3px solid #ececec;
}

.subselections-columns {
  display: flex;
  flex-direction: row;
  gap: 4px;
}

.subselections-column {
  flex: 1;
  min-width: 0;
  max-height: 450px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  border: 1px solid #838383;
  border-radius: 6px;
  padding: 6px 4px 6px 4px;
}

.subselections-column::-webkit-scrollbar {
  display: none;
}

.subselections-column-title {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 700;
  color: #6c757d;
}

.subselections-empty {
  font-size: 13px;
  font-style: italic;
  color: #6c757d;
  padding: 6px 0;
}

.subselection-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #b5b5b5;
  background-color: transparent;
  transition: background-color 0.7s ease;
}

.subselection-row-highlight {
  background-color: #fff3cd;
}

.subselection-move-move {
  transition: transform 0.35s ease;
}

.subselection-move-leave-active {
  transition: none;
}

.subselection-row:last-child {
  border-bottom: none;
}

.subselection-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.subselection-name {
  font-size: 14px;
  font-weight: 600;
  color: #343a40;
  word-break: break-word;
}

.subselection-alias {
  display: flex;
  align-items: flex-start;
  font-weight: 600;
  font-size: 12px;
  color: #60676d;
  word-break: break-word;
}

.subselection-mandatory {
  font-size: 12px;
  color: #6c757d;
}

.subselection-reorder-buttons {
  display: flex;
  flex-direction: row;
  gap: 6px;
  margin-top: 4px;
}

.subselection-reorder-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  width: 22px;
  height: 22px;
  cursor: pointer;
  background: #e2e2e2;
  color: #343a40;
  font-size: 12px;
}

.subselection-button {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  cursor: pointer;
  color: white;
}

.add-subselection-button {
  background: #17b86e;
}

.remove-subselection-button {
  background: #dc3545;
}

@media screen and (max-width: 250px) {
  .subselections-columns {
    flex-direction: column;
  }
}

#edit-product-modal-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 12px 16px;
  background: white;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.14);
  z-index: 20;
}

#bottom-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
}

.footer-button {
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.footer-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

#cancel-product-button {
  background: #e2e2e2;
  color: #343a40;
}

#save-product-button {
  background: #17b86e;
  color: white;
}

@media screen and (max-width: 1018px) {
  #close-product-modal {
    width: 4.5vh;
    min-height: 4.5vh;
    top: 1%;
    margin-right: 8px;
  }

  #selected-product-name {
    margin: 10px 8px 0 8px;
  }
}

@media screen and (max-width: 753px) {
  #selected-product-name {
    font-size: 18px;
  }

  .field-label {
    width: 160px;
  }
}

@media screen and (max-width: 350px) {
  #selected-product-name {
    font-size: 16px;
    margin-right: 13.5%;
  }

  #close-product-modal {
    min-height: 3.5vh;
    width: 3.5vh;
    margin-right: 5%;
  }

  #product-times {
    font-size: 0.85rem;
  }

  .language-row {
    flex-direction: column;
  }

  .language-badge {
    width: auto;
  }

  .field-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .field-label {
    width: auto;
  }
}

@media screen and (max-height: 300px) {
  #product-modal {
    min-height: 100%;
  }

  #selected-product-name {
    font-size: 15px;
  }
}
</style>
